import ExcelJS from "exceljs";

const TEAL_HDR  = "28B3B4";
const TEAL_HDR2 = "1A8E8F";
const TEAL_TEXT = "085041";
const BORDER_CLR = "C8E8E6";
const ROW_EVEN_BG = "F0FAFA";
const ROW_ODD_BG  = "FFFFFF";

const TYPE_STYLES = {
  BIL: { bg: "E1F5EE", fg: "085041" },
  UBL: { bg: "FAFADE", fg: "6B7300" },
};

const STATUS_STYLES = {
  ACTIVE:   { bg: "E1F5EE", fg: "085041" },
  INACTIVE: { bg: "FCEBEB", fg: "791F1F" },
  DRAFT:    { bg: "FAFADE", fg: "6B7300" },
};

const COLUMN_DEFS = [
  { header: "Position Code",    key: "positionCode",    width: 24 },
  { header: "Role Title",       key: "roleTitle",       width: 26 },
  { header: "Type",             key: "positionType",    width: 10 },
  { header: "SOW",              key: "sowName",         width: 28 },
  { header: "MAC",              key: "allocatedToMac",  width: 24 },
  { header: "Associate",        key: "allocatedTo",     width: 26 },
  { header: "FTE",              key: "fte",             width: 10 },
  { header: "Start Date",       key: "startDate",       width: 14 },
  { header: "End Date",         key: "endDate",         width: 14 },
  { header: "Status",           key: "status",          width: 14 },
];

function hairBorder(color = BORDER_CLR) {
  const s = { style: "hair", color: { argb: "FF" + color } };
  return { top: s, bottom: s, left: s, right: s };
}
function mediumBorder(color = TEAL_HDR2) {
  const m = { style: "medium", color: { argb: "FF" + color } };
  const t = { style: "thin",   color: { argb: "FF" + color } };
  return { top: m, bottom: m, left: t, right: t };
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export async function exportPositions(rows = []) {
  // ── Filter: only rows where an associate is allocated ──────────────────────
  const active = rows.filter((r) => r.allocatedTo);

  const wb = new ExcelJS.Workbook();
  wb.creator = "Psi360";
  wb.created = new Date();

  const ws = wb.addWorksheet("Active Positions", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  ws.columns = COLUMN_DEFS.map((c) => ({
    header: c.header,
    key:    c.key,
    width:  c.width,
  }));

  // ── Header row ─────────────────────────────────────────────────────────────
  const headerRow = ws.getRow(1);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font      = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Arial" };
    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + TEAL_HDR } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border    = mediumBorder();
  });

  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to:   { row: 1, column: COLUMN_DEFS.length },
  };

  // ── Data rows ──────────────────────────────────────────────────────────────
  const dataToAdd = active.length > 0 ? active : [{}];

  dataToAdd.forEach((rowData, idx) => {
    const excelRowIdx = idx + 2;
    const isEven      = excelRowIdx % 2 === 0;
    const defaultBg   = isEven ? ROW_EVEN_BG : ROW_ODD_BG;

    const row = ws.addRow(
      COLUMN_DEFS.reduce((acc, col) => {
        if (col.key === "startDate" || col.key === "endDate") {
          acc[col.key] = fmtDate(rowData[col.key]);
        } else {
          acc[col.key] = rowData[col.key] ?? "";
        }
        return acc;
      }, {})
    );
    row.height = 18;

    COLUMN_DEFS.forEach((col, colIdx) => {
      const cell  = row.getCell(colIdx + 1);
      const value = rowData[col.key];

      let bg   = defaultBg;
      let fg   = TEAL_TEXT;
      let bold = false;
      let align = "left";

      if (col.key === "positionType" && TYPE_STYLES[value]) {
        bg = TYPE_STYLES[value].bg; fg = TYPE_STYLES[value].fg;
        bold = true; align = "center";
      } else if (col.key === "status" && STATUS_STYLES[value]) {
        bg = STATUS_STYLES[value].bg; fg = STATUS_STYLES[value].fg;
        bold = true; align = "center";
      } else if (col.key === "fte") {
        align = "center"; bold = true;
      }

      cell.font      = { name: "Arial", size: 10, color: { argb: "FF" + fg }, bold };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + bg } };
      cell.alignment = { horizontal: align, vertical: "middle" };
      cell.border    = hairBorder();
    });
  });

  // ── Summary row at bottom ──────────────────────────────────────────────────
  const summaryRow = ws.addRow({
    positionCode: `Total Active: ${active.length}`,
  });
  summaryRow.height = 18;
  summaryRow.getCell(1).font = {
    name: "Arial", size: 10, bold: true,
    color: { argb: "FF" + TEAL_TEXT },
  };
  summaryRow.getCell(1).fill = {
    type: "pattern", pattern: "solid",
    fgColor: { argb: "FF" + ROW_EVEN_BG },
  };

  // ── Download ───────────────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href    = url;
  const today = new Date().toISOString().split("T")[0];
  a.download  = `active_positions_${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}